import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { FileText, ChevronDown, ChevronUp, AlertTriangle, CheckCircle, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import SignatureCanvas from '../components/SignatureCanvas';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MedicalQuestionnairePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [expandedBoxes, setExpandedBoxes] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [signatureImage, setSignatureImage] = useState(null);
  const [signatureMode, setSignatureMode] = useState('draw');
  const [formData, setFormData] = useState({
    participantName: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '',
    participantEmail: user?.email || '',
    phone: user?.phone || '',
    birthdate: '',
    emergencyContact: '',
    emergencyPhone: '',
    instructorName: '',
    facilityName: 'JustDive',
    medical: {},
    signature: false,
    signatureName: '',
    signatureDate: new Date().toISOString().split('T')[0]
  });

  const handleSignatureChange = (imageData, typedName, mode) => {
    setSignatureMode(mode);
    setSignatureImage(imageData);
    setFormData(prev => ({
      ...prev,
      signature: !!imageData,
      signatureName: typedName || prev.signatureName
    }));
  };

  const toggleBox = (box) => {
    setExpandedBoxes(prev => ({ ...prev, [box]: !prev[box] }));
  };

  const getValue = (field) => formData.medical[field] ?? null;
  const setValue = (field, value) => {
    setFormData(prev => ({
      ...prev,
      medical: { ...prev.medical, [field]: value }
    }));
  };

  const YesNoQuestion = ({ id, question, goToBox = null, hasAsterisk = false }) => {
    const value = getValue(id);
    const showBox = goToBox && value === true;
    
    return (
      <div className="py-3 border-b border-gray-100 last:border-0">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <p className="text-sm text-gray-700">{question}</p>
            {hasAsterisk && <span className="text-xs text-orange-600">* Requires physician evaluation if Yes</span>}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setValue(id, true)}
              className={`px-3 py-1 text-sm rounded-md border transition-colors ${
                value === true 
                  ? 'bg-red-100 border-red-300 text-red-700' 
                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => setValue(id, false)}
              className={`px-3 py-1 text-sm rounded-md border transition-colors ${
                value === false 
                  ? 'bg-green-100 border-green-300 text-green-700' 
                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              No
            </button>
          </div>
        </div>
        {showBox && goToBox && (
          <div className="mt-2 text-xs text-orange-600 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Please answer the questions in Box {goToBox} below
          </div>
        )}
      </div>
    );
  };

  const handleSubmit = async () => {
    // Validate
    if (!formData.participantName || !formData.birthdate) {
      toast.error('Please fill in your name and date of birth');
      return;
    }

    const mainQuestions = ['q1_lungs_heart', 'q2_over_45', 'q3_exercise', 'q4_eyes_ears', 'q5_surgery', 
                          'q6_neurological', 'q7_psychological', 'q8_back_diabetes', 'q9_stomach', 'q10_medications'];
    
    for (const q of mainQuestions) {
      if (formData.medical[q] === undefined || formData.medical[q] === null) {
        toast.error('Please answer all medical screening questions');
        return;
      }
    }

    // Validate signature - need either drawn or typed signature
    if (!signatureImage) {
      toast.error('Please provide your signature (draw or type your name)');
      return;
    }

    try {
      // Save to database with signature image
      await axios.post(`${API_URL}/medical-forms`, {
        ...formData,
        signature_image: signatureImage,
        signature_type: signatureMode,
        submittedAt: new Date().toISOString(),
        userId: user?.id
      });
      
      setSubmitted(true);
      toast.success('Medical questionnaire submitted successfully!');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.success('Medical questionnaire completed! (Saved locally)');
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen py-12 bg-[#FAFAFA]">
        <div className="max-w-2xl mx-auto px-6">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-bold text-[#0A2342] mb-2">
              Medical Questionnaire Submitted
            </h1>
            <p className="text-gray-600 mb-6">
              Thank you for completing the medical questionnaire. Your responses have been recorded.
            </p>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate('/trips')} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90">
                View Trips
              </Button>
              <Button variant="outline" onClick={() => navigate('/liability-waiver')}>
                Complete Liability Waiver
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 bg-[#FAFAFA]">
      <div className="max-w-3xl mx-auto px-6">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-[#0A2342] mb-4">
            Diver Medical Questionnaire
          </h1>
          <p className="text-gray-600">
            Complete this questionnaire as a prerequisite to recreational scuba diving or freediving activities.
          </p>
        </div>

        {/* Download PDF Option */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-2">
              <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium">Need a printable version?</p>
                <p className="text-xs text-blue-700">Download the PDF to complete offline or bring on the day.</p>
              </div>
            </div>
            <a
              href="https://customer-assets.emergentagent.com/job_7d07c68e-45eb-435c-844c-a4650369bb63/artifacts/2dlnkfci_Diver_Medical_Participant_Questionnaire_10346_EN_English_2022-02-01.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-blue-300 rounded-lg text-blue-700 hover:bg-blue-100 transition-colors text-sm font-medium"
            >
              <Download className="w-4 h-4" />
              Download PDF
            </a>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Participant Details */}
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-[#0A2342] mb-4">Participant Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Full Name *</label>
                <Input
                  value={formData.participantName}
                  onChange={(e) => setFormData(prev => ({ ...prev, participantName: e.target.value }))}
                  placeholder="Enter your full name"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Date of Birth *</label>
                <Input
                  type="date"
                  value={formData.birthdate}
                  onChange={(e) => setFormData(prev => ({ ...prev, birthdate: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Email</label>
                <Input
                  type="email"
                  value={formData.participantEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, participantEmail: e.target.value }))}
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Phone Number</label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+27 XX XXX XXXX"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Emergency Contact Name *</label>
                <Input
                  value={formData.emergencyContact}
                  onChange={(e) => setFormData(prev => ({ ...prev, emergencyContact: e.target.value }))}
                  placeholder="Name of emergency contact"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Emergency Contact Phone *</label>
                <Input
                  type="tel"
                  value={formData.emergencyPhone}
                  onChange={(e) => setFormData(prev => ({ ...prev, emergencyPhone: e.target.value }))}
                  placeholder="+27 XX XXX XXXX"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Instructor Name (if applicable)</label>
                <Input
                  value={formData.instructorName}
                  onChange={(e) => setFormData(prev => ({ ...prev, instructorName: e.target.value }))}
                  placeholder="Instructor name"
                />
              </div>
            </div>
          </div>

          {/* Warning Note */}
          <div className="p-4 bg-yellow-50 border-b border-yellow-200">
            <p className="text-sm text-yellow-800">
              <strong>Note to women:</strong> If you are pregnant, or attempting to become pregnant, do not dive.
            </p>
          </div>

          {/* Main Questions */}
          <div className="p-6">
            <h2 className="text-lg font-semibold text-[#0A2342] mb-4">Health Screening Questions</h2>
            
            <YesNoQuestion 
              id="q1_lungs_heart" 
              question="1. I have had problems with my lungs, breathing, heart and/or blood affecting my normal physical or mental performance."
              goToBox="A"
            />
            
            <YesNoQuestion 
              id="q2_over_45" 
              question="2. I am over 45 years of age."
              goToBox="B"
            />
            
            <YesNoQuestion 
              id="q3_exercise" 
              question="3. I struggle to perform moderate exercise (walk 1.6km in 14 minutes or swim 200m without resting), OR I have been unable to participate in normal physical activity due to fitness or health reasons within the past 12 months."
              hasAsterisk
            />
            
            <YesNoQuestion 
              id="q4_eyes_ears" 
              question="4. I have had problems with my eyes, ears, or nasal passages/sinuses."
              goToBox="C"
            />
            
            <YesNoQuestion 
              id="q5_surgery" 
              question="5. I have had surgery within the last 12 months, OR I have ongoing problems related to past surgery."
              hasAsterisk
            />
            
            <YesNoQuestion 
              id="q6_neurological" 
              question="6. I have lost consciousness, had migraine headaches, seizures, stroke, significant head injury, or suffer from persistent neurologic injury or disease."
              goToBox="D"
            />
            
            <YesNoQuestion 
              id="q7_psychological" 
              question="7. I am currently undergoing treatment (or have required treatment within the last five years) for psychological problems, personality disorder, panic attacks, or an addiction to drugs or alcohol; or, I have been diagnosed with a learning or developmental disability."
              goToBox="E"
            />
            
            <YesNoQuestion 
              id="q8_back_diabetes" 
              question="8. I have had back problems, hernia, ulcers, or diabetes."
              goToBox="F"
            />
            
            <YesNoQuestion 
              id="q9_stomach" 
              question="9. I have had stomach or intestine problems, including recent diarrhea."
              goToBox="G"
            />
            
            <YesNoQuestion 
              id="q10_medications" 
              question="10. I am taking prescription medications (with the exception of birth control or anti-malarial drugs other than mefloquine/Lariam)."
              hasAsterisk
            />
          </div>

          {/* Conditional Boxes */}
          <div className="px-6 pb-6 space-y-4">
            {/* Box A */}
            {getValue('q1_lungs_heart') === true && (
              <div className="border border-orange-200 rounded-lg overflow-hidden">
                <button type="button" onClick={() => toggleBox('A')} className="w-full flex items-center justify-between p-3 bg-orange-50 hover:bg-orange-100">
                  <span className="font-medium text-orange-800">Box A - Heart/Lungs Questions</span>
                  {expandedBoxes.A ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {expandedBoxes.A && (
                  <div className="p-4 bg-white">
                    <YesNoQuestion id="boxA_1" question="Chest surgery, heart surgery, heart valve surgery, an implantable medical device (stent, pacemaker, neurostimulator), pneumothorax, and/or chronic lung disease." hasAsterisk />
                    <YesNoQuestion id="boxA_2" question="Asthma, wheezing, severe allergies, hay fever or congested airways within the last 12 months that limits my physical activity/exercise." hasAsterisk />
                    <YesNoQuestion id="boxA_3" question="A problem or illness involving my heart such as: angina, chest pain on exertion, heart failure, immersion pulmonary edema, heart attack or stroke, OR am taking medication for any heart condition." hasAsterisk />
                    <YesNoQuestion id="boxA_4" question="Recurrent bronchitis and currently coughing within the past 12 months, OR have been diagnosed with emphysema." hasAsterisk />
                    <YesNoQuestion id="boxA_5" question="Symptoms affecting my lungs, breathing, heart and/or blood in the last 30 days that impair my physical or mental performance." hasAsterisk />
                  </div>
                )}
              </div>
            )}

            {/* Box B */}
            {getValue('q2_over_45') === true && (
              <div className="border border-orange-200 rounded-lg overflow-hidden">
                <button type="button" onClick={() => toggleBox('B')} className="w-full flex items-center justify-between p-3 bg-orange-50 hover:bg-orange-100">
                  <span className="font-medium text-orange-800">Box B - Over 45 Questions</span>
                  {expandedBoxes.B ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {expandedBoxes.B && (
                  <div className="p-4 bg-white">
                    <YesNoQuestion id="boxB_1" question="I currently smoke or inhale nicotine by other means." hasAsterisk />
                    <YesNoQuestion id="boxB_2" question="I have a high cholesterol level." hasAsterisk />
                    <YesNoQuestion id="boxB_3" question="I have high blood pressure." hasAsterisk />
                    <YesNoQuestion id="boxB_4" question="I have had a close blood relative die suddenly or of cardiac disease or stroke before the age of 50, OR have a family history of heart disease before age 50." hasAsterisk />
                  </div>
                )}
              </div>
            )}

            {/* Box C */}
            {getValue('q4_eyes_ears') === true && (
              <div className="border border-orange-200 rounded-lg overflow-hidden">
                <button type="button" onClick={() => toggleBox('C')} className="w-full flex items-center justify-between p-3 bg-orange-50 hover:bg-orange-100">
                  <span className="font-medium text-orange-800">Box C - Eyes/Ears Questions</span>
                  {expandedBoxes.C ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {expandedBoxes.C && (
                  <div className="p-4 bg-white">
                    <YesNoQuestion id="boxC_1" question="Sinus surgery within the last 6 months." hasAsterisk />
                    <YesNoQuestion id="boxC_2" question="Ear disease or ear surgery, hearing loss, or problems with balance." hasAsterisk />
                    <YesNoQuestion id="boxC_3" question="Recurrent sinusitis within the past 12 months." hasAsterisk />
                    <YesNoQuestion id="boxC_4" question="Eye surgery within the past 3 months." hasAsterisk />
                  </div>
                )}
              </div>
            )}

            {/* Box D */}
            {getValue('q6_neurological') === true && (
              <div className="border border-orange-200 rounded-lg overflow-hidden">
                <button type="button" onClick={() => toggleBox('D')} className="w-full flex items-center justify-between p-3 bg-orange-50 hover:bg-orange-100">
                  <span className="font-medium text-orange-800">Box D - Neurological Questions</span>
                  {expandedBoxes.D ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {expandedBoxes.D && (
                  <div className="p-4 bg-white">
                    <YesNoQuestion id="boxD_1" question="Head injury with loss of consciousness within the past 5 years." hasAsterisk />
                    <YesNoQuestion id="boxD_2" question="Persistent neurologic injury or disease." hasAsterisk />
                    <YesNoQuestion id="boxD_3" question="Recurring migraine headaches within the past 12 months, or take medications to prevent them." hasAsterisk />
                    <YesNoQuestion id="boxD_4" question="Blackouts or fainting (full/partial loss of consciousness) within the last 5 years." hasAsterisk />
                    <YesNoQuestion id="boxD_5" question="Epilepsy, seizures, or convulsions, OR take medications to prevent them." hasAsterisk />
                  </div>
                )}
              </div>
            )}

            {/* Box E */}
            {getValue('q7_psychological') === true && (
              <div className="border border-orange-200 rounded-lg overflow-hidden">
                <button type="button" onClick={() => toggleBox('E')} className="w-full flex items-center justify-between p-3 bg-orange-50 hover:bg-orange-100">
                  <span className="font-medium text-orange-800">Box E - Psychological Questions</span>
                  {expandedBoxes.E ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {expandedBoxes.E && (
                  <div className="p-4 bg-white">
                    <YesNoQuestion id="boxE_1" question="Behavioral health, mental or psychological problems requiring medical/psychiatric treatment." hasAsterisk />
                    <YesNoQuestion id="boxE_2" question="Major depression, suicidal ideation, panic attacks, uncontrolled bipolar disorder requiring medication/psychiatric treatment." hasAsterisk />
                    <YesNoQuestion id="boxE_3" question="Been diagnosed with a mental health condition or a learning/developmental disorder that requires ongoing care or special accommodation." hasAsterisk />
                    <YesNoQuestion id="boxE_4" question="An addiction to drugs or alcohol requiring treatment within the last 5 years." hasAsterisk />
                  </div>
                )}
              </div>
            )}

            {/* Box F */}
            {getValue('q8_back_diabetes') === true && (
              <div className="border border-orange-200 rounded-lg overflow-hidden">
                <button type="button" onClick={() => toggleBox('F')} className="w-full flex items-center justify-between p-3 bg-orange-50 hover:bg-orange-100">
                  <span className="font-medium text-orange-800">Box F - Back/Diabetes Questions</span>
                  {expandedBoxes.F ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {expandedBoxes.F && (
                  <div className="p-4 bg-white">
                    <YesNoQuestion id="boxF_1" question="Recurrent back problems in the last 6 months that limit my everyday activity." hasAsterisk />
                    <YesNoQuestion id="boxF_2" question="Back or spinal surgery within the last 12 months." hasAsterisk />
                    <YesNoQuestion id="boxF_3" question="Diabetes, either drug or diet controlled, OR gestational diabetes within the last 12 months." hasAsterisk />
                    <YesNoQuestion id="boxF_4" question="An uncorrected hernia that limits my physical abilities." hasAsterisk />
                    <YesNoQuestion id="boxF_5" question="Active or untreated ulcers, problem wounds, or ulcer surgery within the last 6 months." hasAsterisk />
                  </div>
                )}
              </div>
            )}

            {/* Box G */}
            {getValue('q9_stomach') === true && (
              <div className="border border-orange-200 rounded-lg overflow-hidden">
                <button type="button" onClick={() => toggleBox('G')} className="w-full flex items-center justify-between p-3 bg-orange-50 hover:bg-orange-100">
                  <span className="font-medium text-orange-800">Box G - Stomach/Intestine Questions</span>
                  {expandedBoxes.G ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {expandedBoxes.G && (
                  <div className="p-4 bg-white">
                    <YesNoQuestion id="boxG_1" question="Ostomy surgery and do not have medical clearance to swim or engage in physical activity." hasAsterisk />
                    <YesNoQuestion id="boxG_2" question="Dehydration requiring medical intervention within the last 7 days." hasAsterisk />
                    <YesNoQuestion id="boxG_3" question="Active or untreated stomach or intestinal ulcers or ulcer surgery within the last 6 months." hasAsterisk />
                    <YesNoQuestion id="boxG_4" question="Frequent heartburn, regurgitation, or gastroesophageal reflux disease (GERD)." hasAsterisk />
                    <YesNoQuestion id="boxG_5" question="Active or uncontrolled ulcerative colitis or Crohn's disease." hasAsterisk />
                    <YesNoQuestion id="boxG_6" question="Bariatric surgery within the last 12 months." hasAsterisk />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Signature Section */}
          <div className="p-6 border-t bg-gray-50">
            <h2 className="text-lg font-semibold text-[#0A2342] mb-4">Participant Declaration & Signature</h2>
            
            <p className="text-sm text-gray-600 mb-4">
              I have answered all questions honestly, and understand that I accept responsibility for any consequences 
              resulting from any questions I may have answered inaccurately or for my failure to disclose any existing or past health conditions.
            </p>

            <div className="bg-white border rounded-lg p-4 space-y-4">
              {/* Signature Canvas - Draw or Type */}
              <SignatureCanvas 
                onSignatureChange={handleSignatureChange}
                width={400}
                height={120}
                label="Your Signature"
                initialTypedName={formData.signatureName}
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Signature Date *</label>
                  <Input
                    type="date"
                    value={formData.signatureDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, signatureDate: e.target.value }))}
                  />
                </div>
              </div>
              
              <label className="flex items-start gap-3 p-3 bg-gray-50 rounded border cursor-pointer hover:bg-gray-100">
                <input
                  type="checkbox"
                  checked={formData.signature && !!signatureImage}
                  disabled
                  className="mt-1 w-4 h-4 text-[#0A2342] rounded"
                />
                <span className="text-sm text-gray-700">
                  {signatureImage ? (
                    <span className="text-green-600 font-medium">Signature captured - By signing above, I confirm this constitutes my legal electronic signature.</span>
                  ) : (
                    'Please sign above (draw or type your name) to confirm your declaration.'
                  )}
                </span>
              </label>
            </div>

            <div className="mt-6 flex justify-end">
              <Button 
                onClick={handleSubmit}
                className="bg-[#FF6B35] hover:bg-[#FF6B35]/90 px-8"
                data-testid="submit-medical-form"
              >
                Submit Questionnaire
              </Button>
            </div>
          </div>
        </div>

        {/* Medical Guidance Link */}
        <div className="mt-6 text-center">
          <a
            href="https://customer-assets.emergentagent.com/job_7d07c68e-45eb-435c-844c-a4650369bb63/artifacts/0vxx3q95_Diving_Medical_Guidance_EN_English_2023-05-31.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-[#FF6B35] hover:underline"
          >
            View Medical Guidance Document (PDF)
          </a>
        </div>
      </div>
    </div>
  );
};

export default MedicalQuestionnairePage;
